// Which Quick log tiles matter right now: "now" (due — tap me next), "soon"
// (coming up in the next quarter-hour or so) or nothing. Each comes with the
// tile's subtitle, so the tile says why ("due in 9m", "after his meal").

import { MINUTE, formatTime } from './time.ts';
import type { Urgency } from './status.ts';
import type { Millis } from './types.ts';

/** Out this recently at bedtime: that was his last wee, straight to bed. */
export const BED_JUST_OUT = 20 * MINUTE;
/** Out within this at bedtime: a top-up wee is optional, not a must. */
export const BED_TOPUP = 60 * MINUTE;

export type TileId = 'wee' | 'poo' | 'meal' | 'nap' | 'play' | 'training';
export type Level = 'now' | 'soon';

export interface Relevance {
  level: Level;
  sub: string;
}

export interface RelevanceInput {
  now: Millis;
  /** Overnight: only the toilet matters — no meals, naps, games or training. */
  night?: boolean;
  /** Past bedtime and still up (domain/sleep.ts bedtimePending): last wee, a poo if he's eaten, then bed. */
  bedtime?: boolean;
  asleep: boolean;
  /** Toilet prediction (none while asleep, or when it's guesswork). */
  toiletDueAt?: Millis;
  toiletUrgency: Urgency;
  /** Why it's due ("Just woke up", "After his meal"…) — the same prediction the Now card shows. */
  toiletReason?: string;
  toiletStale?: boolean;
  napDueAt?: Millis;
  napUrgency: Urgency;
  /** Naps weren't being logged — nap times are guesswork. */
  sleepStale?: boolean;
  wokeAt?: Millis;
  lastWeeAt?: Millis;
  lastPooAt?: Millis;
  lastMealAt?: Millis;
  /** The next planned meal (may be one just missed). */
  nextMealAt?: Millis;
  trainingToday: number;
  trainingTarget: number;
}

const inMins = (ms: Millis) => {
  const m = Math.max(1, Math.round(ms / MINUTE));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

export function tileRelevance(x: RelevanceInput): Partial<Record<TileId, Relevance>> {
  const out: Partial<Record<TileId, Relevance>> = {};
  if (x.asleep) return out;
  const { now } = x;
  // Any wee or poo since he woke counts: he's been out (a poo trip is a toilet trip).
  const lastToilet = Math.max(x.lastWeeAt ?? 0, x.lastPooAt ?? 0);
  const outSinceWoke = x.wokeAt !== undefined && lastToilet >= x.wokeAt;

  // Wee: exactly when the Now card's toilet prediction says (straight after waking, after a
  // meal, the regular gap) — one source, so the tile and the card can't disagree.
  if (!x.toiletStale && x.toiletDueAt !== undefined) {
    const left = x.toiletDueAt - now;
    const why = x.toiletReason === 'Just woke up' ? 'just woke' : x.toiletReason === 'After his meal' ? 'after his meal' : undefined;
    if (x.toiletUrgency === 'now' || x.toiletUrgency === 'overdue' || left <= 0) out.wee = { level: 'now', sub: left < -5 * MINUTE ? `${inMins(-left)} over` : (why ?? 'due now') };
    else if (left <= 15 * MINUTE) out.wee = { level: 'soon', sub: `due in ${inMins(left)}` };
  }

  // Poo: most puppies go 5–30 minutes after a meal — unless he went just
  // before it (a poo at 07:41 and breakfast at 07:43 means he's empty).
  if (x.lastMealAt !== undefined && (x.lastPooAt === undefined || x.lastPooAt < x.lastMealAt - 60 * MINUTE)) {
    const since = now - x.lastMealAt;
    if (since >= 5 * MINUTE && since <= 40 * MINUTE) out.poo = { level: since >= 10 * MINUTE ? 'now' : 'soon', sub: 'after his meal' };
  }

  // Meal: around its time, and not for one missed hours ago.
  if (x.nextMealAt !== undefined) {
    const left = x.nextMealAt - now;
    if (left <= 0 && left > -45 * MINUTE) out.meal = { level: 'now', sub: 'due now' };
    else if (left > 0 && left <= 20 * MINUTE) out.meal = { level: 'soon', sub: `due in ${inMins(left)}` };
  }

  // Nap: when his awake window is nearly up.
  if (!x.sleepStale && x.napDueAt !== undefined) {
    const left = x.napDueAt - now;
    if (x.napUrgency === 'now' || x.napUrgency === 'overdue' || left <= 0) out.nap = { level: 'now', sub: left < -5 * MINUTE ? `${inMins(-left)} overdue` : 'due now' };
    else if (left <= 15 * MINUTE) out.nap = { level: 'soon', sub: `due in ${inMins(left)}` };
  }

  // Fresh, emptied and not near a nap: the best few minutes for training (or play).
  const fresh = x.wokeAt !== undefined && !x.sleepStale && outSinceWoke && now - x.wokeAt >= 5 * MINUTE && !out.nap && !out.wee && !out.poo && !out.meal;
  if (fresh && (x.napDueAt === undefined || x.napDueAt - now > 20 * MINUTE)) {
    if (x.trainingToday < x.trainingTarget) out.training = { level: 'soon', sub: `good time · ${x.trainingToday}/${x.trainingTarget} today` };
    else out.play = { level: 'soon', sub: 'good time for a game' };
  }
  if (x.bedtime) {
    // The bedtime routine: a last trip out, a poo if he's eaten since his last one, then bed.
    // Out in the last 20 minutes counts as his last wee; within the hour, a top-up is optional
    // (and says when he last went, so it doesn't look like his wee was missed).
    const out2: Partial<Record<TileId, Relevance>> = {};
    const sinceOut = now - lastToilet;
    if (sinceOut > BED_TOPUP) out2.wee = { level: 'now', sub: 'before bed' };
    else if (sinceOut > BED_JUST_OUT) out2.wee = { level: 'soon', sub: `went ${formatTime(lastToilet)}` };
    if (x.lastMealAt !== undefined && now - x.lastMealAt < 3 * 60 * MINUTE && (x.lastPooAt ?? 0) < x.lastMealAt) out2.poo = { level: 'soon', sub: 'if he needs one' };
    out2.nap = { level: 'now', sub: 'lights low' };
    return out2;
  }
  if (x.night) for (const k of ['meal', 'nap', 'training', 'play'] as const) delete out[k];
  return out;
}
